import {
  classifyNavigationRoute,
  validAppRoute,
} from '@/convex/products/ai/navigation/destination_routes.helpers';
import { WORKSPACE_FILES_NODE_REFERENCE_PARAM } from '@/convex/products/ai/navigation/workspace_entity_routes.helpers';
import {
  parseWorkspaceNodeReference,
  type WorkspaceNodeReference,
} from '@/lib/workspace-node-reference';

export type RealtimeVoiceWorkspaceLocation = {
  path: string | null;
  nodeReference?: WorkspaceNodeReference;
};

/**
 * The workspace folder or file the user is viewing, read from the current
 * URL when a voice tool runs. It is sent only with a requested workspace
 * command, never streamed as passive page context. Returns null outside the
 * Files viewer or an entity detail page. The server resolves node identity and
 * authorization; neither the pathname nor a reference's pathHint grants access.
 */
export function realtimeVoiceCurrentWorkspaceLocation(
  href: string
): RealtimeVoiceWorkspaceLocation {
  try {
    if (!href.startsWith('/') || href.startsWith('//')) return { path: null };
    const url = new URL(href, 'https://app.invalid');
    const segments = url.pathname.split('/').filter(Boolean);
    const routeOrg = segments[0];
    if (!routeOrg) return { path: null };
    const orgSlug = decodeURIComponent(routeOrg);
    if (!validAppRoute(href, orgSlug)) return { path: null };
    let nodeReference: WorkspaceNodeReference | null = null;
    const rawReference = url.searchParams.get(
      WORKSPACE_FILES_NODE_REFERENCE_PARAM
    );
    if (segments[1] === 'files' && rawReference !== null) {
      nodeReference = parseWorkspaceNodeReference(JSON.parse(rawReference));
      if (!nodeReference) return { path: null };
      // The Files viewer uses the node selector instead of its avatar detail UI.
      url.searchParams.delete('avatarPath');
    }
    const route = classifyNavigationRoute(
      `${url.pathname}${url.search}${url.hash}`,
      orgSlug
    );
    if (!route || (route.kind !== 'file' && route.kind !== 'entity')) {
      return { path: null };
    }
    // An avatar detail URL without its folder selector is not a VFS path.
    if (
      !nodeReference &&
      segments[1] === 'files' &&
      segments[2] === 'avatars' &&
      segments.length === 4 &&
      route.kind !== 'entity'
    ) {
      return { path: null };
    }
    return {
      path: route.path,
      ...(nodeReference ? { nodeReference } : {}),
    };
  } catch {
    return { path: null };
  }
}

export function realtimeVoiceCurrentWorkspacePath(href: string): string | null {
  return realtimeVoiceCurrentWorkspaceLocation(href).path;
}
